const LOOP_STEPS_EN = [
  {
    id: 'discover',
    title: 'Discover',
    chip: 'Value, not hype',
    body:
      'We validate AI use cases with real business value — turning an opportunity into a clear use case with objectives, data, users, success criteria and outcomes.',
  },
  {
    id: 'build',
    title: 'Build',
    chip: 'Proof in weeks',
    body:
      'We build a working model tailored to your exact problem. Our team sets up the background software making sure the system reads text perfectly and matches data accurately, while keeping your team in control to test and approve it.',
  },
  {
    id: 'deploy',
    title: 'Deploy',
    chip: 'Production-grade',
    body:
      'Integrated into your systems and processes, with the technical controls AI governance demands — logging, audit, access control, versioning and monitoring.',
  },
  {
    id: 'operate',
    title: 'Operate & Scale',
    chip: 'Compounding value',
    body:
      'We harden, operate, measure and evolve — tracking impact against the baseline and feeding the learning back into the next loop. One loop becomes a capability. A capability becomes an advantage.',
  },
]

const LOOP_STEPS_PT = [
  {
    id: 'discover',
    title: 'Descobrir',
    chip: 'Valor, não hype',
    body:
      'Validamos casos de uso de IA com valor de negócio real, transformando uma oportunidade num caso de uso claro, com objetivos, dados, utilizadores, critérios de sucesso e resultados.',
  },
  {
    id: 'build',
    title: 'Construir',
    chip: 'Prova em semanas',
    body:
      'Construímos um modelo funcional à medida do seu problema. A nossa equipa configura o software de suporte, garantindo leitura precisa de texto e correspondência fiável de dados, mantendo a sua equipa no controlo para testar e aprovar.',
  },
  {
    id: 'deploy',
    title: 'Implementar',
    chip: 'Pronto para produção',
    body:
      'Integrado nos seus sistemas e processos, com os controlos técnicos que a governação de IA exige, logging, registos para auditoria, controlo de acessos, versionamento e monitorização.',
  },
  {
    id: 'operate',
    title: 'Operar e Escalar',
    chip: 'Valor composto',
    multiline: true,
    body:
      'Robustecemos, operamos, medimos e evoluímos, acompanhando o impacto contra a baseline e realimentando a aprendizagem no loop seguinte. Um loop torna-se uma capacidade. Uma capacidade torna-se uma vantagem.',
  },
]

const FDE_CAPABILITIES_EN = [
  {
    id: 'automation',
    label: 'Document & process automation',
    body:
      'Turn unstructured documents and manual workflows into straight-through processing — extraction, classification, validation, with humans only where they add value.',
  },
  {
    id: 'rag',
    label: 'Knowledge & RAG systems',
    body:
      'Assistants grounded in your trusted sources, so teams get accurate, cited answers from policies, manuals and records — without hallucinations and without exposing data you do not want exposed.',
  },
  {
    id: 'data',
    label: 'Data & infrastructure',
    body:
      'The pipelines, vector stores and integration layers that make AI dependable — built on your stack, your cloud and your security model, designed to be operated, not demoed.',
  },
  {
    id: 'governance',
    label: 'AI governance & compliance',
    body:
      'Logging, audit trails, access control, versioning and monitoring wrapped around every deployment — aligned with DORA, the EU AI Act and CNPD, ready for your auditors from day one.',
  },
]

const FDE_CAPABILITIES_PT = [
  {
    id: 'automation',
    label: 'Automação documental e de processos',
    body:
      'Transformamos documentos não estruturados e fluxos manuais em processamento direto, extração, classificação, validação, com humanos apenas onde acrescentam valor.',
  },
  {
    id: 'rag',
    label: 'Conhecimento & sistemas RAG',
    body:
      'Assistentes fundamentados nas suas fontes de confiança, para que as equipas obtenham respostas exatas e citadas a partir de políticas, manuais e registos, sem alucinações e sem expor dados que não quer expostos.',
  },
  {
    id: 'data',
    label: 'Dados & infraestrutura',
    body:
      'Os pipelines, vector stores e camadas de integração que tornam a IA fiável, construídos sobre a sua stack, a sua cloud e o seu modelo de segurança, desenhados para serem operados, não demonstrados.',
  },
  {
    id: 'governance',
    label: 'Governação de IA & compliance',
    body:
      'Logging, registos para auditoria, controlo de acessos, versionamento e monitorização em torno de cada implementação, alinhados com o DORA, o AI Act europeu e a CNPD, prontos para os seus auditores desde o primeiro dia.',
  },
]

const USE_CASES_SOLUTION_EN = [
  {
    id: 'doc',
    category: 'Document Automation',
    badge: '−80% Manual Effort',
    title: 'Intelligent document processing',
    kpi: 'Straight-through processing at scale',
    description:
      'Ingest forms, contracts and invoices; extract, classify and validate with OCR, vision models and human-in-the-loop only where it counts.',
    icon: 'circle-amber',
  },
  {
    id: 'rag',
    category: 'Knowledge & RAG',
    badge: '3× Faster Answers',
    title: 'Knowledge assistant on your data',
    kpi: 'Faster, better-informed decisions',
    description:
      'A RAG system grounded in your trusted sources, so teams get accurate, cited answers from policies, manuals and records — without hallucinations.',
    icon: 'triangle-amber',
  },
  {
    id: 'agents',
    category: 'Agents & Orchestration',
    badge: 'Guardrailed',
    title: 'Agentic process orchestration',
    kpi: 'Multi-step work handled end to end',
    description:
      'AI agents that execute multi-step workflows — triage, drafting, reconciliation, follow-ups — with approval gates, full audit trails and humans on the exceptions.',
    icon: 'pill-green',
  },
  {
    id: 'support',
    category: 'Customer Operations',
    badge: '−50% Handling Time',
    title: 'Customer support copilots',
    kpi: 'Lower cost-to-serve, higher CSAT',
    description:
      'Conversational assistants and agent copilots that resolve routine requests and surface the right context, while keeping humans in control.',
    icon: 'diamond-purple',
  },
  {
    id: 'procurement',
    category: 'Procurement & Contracts',
    badge: 'Leakage-Proof',
    title: 'Procurement & contract intelligence',
    kpi: 'Catch discrepancies before they cost',
    description:
      'Match purchase orders, invoices and goods receipts; extract obligations and renewal dates from contracts; flag deviations before they become losses.',
    icon: 'diamond-cyan',
  },
  {
    id: 'forecasting',
    category: 'Forecasting & ML',
    badge: 'Decision-Grade',
    title: 'Predictive models & optimisation',
    kpi: 'Fewer errors, leaner inventory',
    description:
      'Demand, risk and pricing models — classical ML and optimisation for the problems where statistics still beat a prompt.',
    icon: 'circle-orange',
  },
  {
    id: 'engineering',
    category: 'Engineering & Legacy',
    badge: 'De-Risked',
    title: 'AI-assisted modernisation',
    kpi: 'Months of manual work removed',
    description:
      'Accelerate legacy migrations and code understanding — documentation, test generation and translation that de-risk the move and speed the team up.',
    icon: 'triangle-cyan',
  },
  {
    id: 'gov',
    category: 'Governance',
    badge: 'Audit-Ready',
    title: 'AI governance layer',
    kpi: 'Scale value without scaling risk',
    description:
      'Logging, audit trails, access control, versioning and monitoring wrapped around every deployment — aligned with DORA, the EU AI Act and CNPD.',
    icon: 'square-red',
  },
]

const USE_CASES_SOLUTION_PT = [
  {
    id: 'doc',
    category: 'Automação Documental',
    badge: '−80% Esforço Manual',
    title: 'Processamento inteligente de documentos',
    kpi: 'Processamento direto em escala',
    description:
      'Ingestão de formulários, contratos e faturas; extração, classificação e validação com OCR, modelos de visão e human-in-the-loop apenas onde conta.',
    icon: 'circle-amber',
  },
  {
    id: 'rag',
    category: 'Conhecimento & RAG',
    badge: 'Respostas 3× Mais Rápidas',
    title: 'Assistente de conhecimento sobre os seus dados',
    kpi: 'Decisões mais rápidas e mais informadas',
    description:
      'Um sistema RAG fundamentado nas suas fontes de confiança, para que as equipas obtenham respostas exatas e citadas a partir de políticas, manuais e registos, sem alucinações.',
    icon: 'triangle-amber',
  },
  {
    id: 'agents',
    category: 'Agentes & Orquestração',
    badge: 'Com Guardrails',
    title: 'Orquestração agêntica de processos',
    kpi: 'Trabalho multi-etapa tratado de ponta a ponta',
    description:
      'Agentes de IA que executam fluxos multi-etapa, triagem, redação, reconciliação, follow-ups, com padrões de aprovação, registos para auditoria completos e humanos nas exceções.',
    icon: 'pill-green',
  },
  {
    id: 'support',
    category: 'Operações de Cliente',
    badge: '−50% Tempo de Tratamento',
    title: 'Copilotos de apoio ao cliente',
    kpi: 'Menor custo de servir, maior CSAT',
    description:
      'Assistentes conversacionais e agentes copiloto que resolvem pedidos de rotina e trazem o contexto certo, mantendo os humanos no controlo.',
    icon: 'diamond-purple',
  },
  {
    id: 'procurement',
    category: 'Procurement & Contratos',
    badge: 'Sem Fugas de Valor',
    title: 'Inteligência de procurement e contratos',
    kpi: 'Apanhar discrepâncias antes de custarem',
    description:
      'Cruzamento de ordens de compra, faturas e guias de receção; extração de obrigações e datas de renovação dos contratos; sinalização de desvios antes de se tornarem perdas.',
    icon: 'diamond-cyan',
  },
  {
    id: 'forecasting',
    category: 'Previsão & ML',
    badge: 'Nível de Decisão',
    title: 'Modelos preditivos & otimização',
    kpi: 'Menos erros, inventário mais magro',
    description:
      'Modelos de procura, risco e preço, ML clássico e otimização para os problemas onde a estatística ainda vence um prompt.',
    icon: 'circle-orange',
  },
  {
    id: 'engineering',
    category: 'Engenharia & Legado',
    badge: 'Risco Controlado',
    title: 'Modernização assistida por IA',
    kpi: 'Meses de trabalho manual eliminados',
    description:
      'Acelerar as migrações de sistemas legados e a compreensão do código — documentação, criação de testes e tradução que minimizam os riscos da transição e aceleram o trabalho da equipa.',
    icon: 'triangle-cyan',
  },
  {
    id: 'gov',
    category: 'Governação',
    badge: 'Pronto para Auditoria',
    title: 'Camada de governação de IA',
    kpi: 'Escalar valor sem escalar risco',
    description:
      'Logging, registos para auditoria, controlo de acessos, versionamento e monitorização em torno de cada implementação, alinhados com o DORA, o AI Act europeu e a CNPD.',
    icon: 'square-red',
  },
]

const USE_CASES_INDUSTRY_EN = [
  {
    id: 'insurance',
    category: 'Insurance & Finance',
    badge: 'Fully Traceable',
    title: 'Claims & document operations',
    kpi: 'Faster cycle times, lower cost per file',
    description:
      'Automated extraction and validation across high-volume document flows — claims, underwriting submissions, KYC, onboarding — with full traceability for regulated environments.',
    icon: 'circle-amber',
  },
  {
    id: 'retail',
    category: 'Retail & E-commerce',
    badge: 'Catalogue Scale',
    title: 'Commerce intelligence',
    kpi: 'More revenue per visit, less manual ops',
    description:
      'Product enrichment, search and recommendations, and content automation for catalogues at scale — built on deep commerce and marketplace experience.',
    icon: 'triangle-amber',
  },
  {
    id: 'auto',
    category: 'Automotive & Distribution',
    badge: 'Multi-Brand Ready',
    title: 'Dealer & aftersales operations',
    kpi: 'One operating picture across brands',
    description:
      'Unify DMS, stock, pricing and aftersales data across brands and locations; automate document flows, parts logistics and service scheduling on top of fragmented systems.',
    icon: 'pill-green',
  },
  {
    id: 'telecom',
    category: 'Telecom & Media',
    badge: 'End-to-End',
    title: 'Conversational customer service',
    kpi: 'Deflected calls, sustained CSAT',
    description:
      'Voice and chat assistants that handle billing and support intents end to end, integrated with existing systems — automation without losing service quality.',
    icon: 'diamond-purple',
  },
  {
    id: 'logistics',
    category: 'Logistics & Transport',
    badge: 'On-Time',
    title: 'Supply chain & inventory intelligence',
    kpi: 'Less stock, fewer stockouts',
    description:
      'Demand sensing, inventory rebalancing and exception management across the network — recommendations grounded in your data, executed with approval gates.',
    icon: 'diamond-cyan',
  },
  {
    id: 'industrial',
    category: 'Industrial & Energy',
    badge: 'Predictive',
    title: 'Operations & maintenance copilots',
    kpi: 'Higher asset uptime and readiness',
    description:
      'Bring sensor data, maintenance logs and manuals into one place; predict failures and put answers in the hands of field teams before issues escalate.',
    icon: 'square-red',
  },
  {
    id: 'healthcare',
    category: 'Healthcare & Life Sciences',
    badge: 'Unified, Real-Time',
    title: 'Research & clinical data platforms',
    kpi: 'Less duplication, faster research',
    description:
      'Unify fragmented research and operational data into structured, real-time access — reducing duplication and accelerating scientific and clinical workflows.',
    icon: 'circle-orange',
  },
  {
    id: 'public',
    category: 'Public Sector',
    badge: 'Governed by Default',
    title: 'Citizen & case processing',
    kpi: 'Shorter queues, full auditability',
    description:
      'Automate intake, classification and routing of requests and documents, with the audit and access controls public institutions require.',
    icon: 'triangle-cyan',
  },
]

const USE_CASES_INDUSTRY_PT = [
  {
    id: 'insurance',
    category: 'Seguros & Finança',
    badge: 'Totalmente Rastreável',
    title: 'Operações de sinistros e documentos',
    kpi: 'Ciclos mais rápidos, menor custo por processo',
    description:
      'Extração e validação automatizadas em fluxos documentais de alto volume, sinistros, propostas de subscrição, KYC, onboarding, com rastreabilidade total para ambientes regulados.',
    icon: 'circle-amber',
  },
  {
    id: 'retail',
    category: 'Retalho & E-commerce',
    badge: 'À Escala do Catálogo',
    title: 'Inteligência de comércio',
    kpi: 'Mais receita por visita, menos operação manual',
    description:
      'Enriquecimento de produto, pesquisa e recomendações, e automação de conteúdo para catálogos em escala, com experiência profunda em comércio e marketplaces.',
    icon: 'triangle-amber',
  },
  {
    id: 'auto',
    category: 'Automóvel & Distribuição',
    badge: 'Multi-Marca',
    title: 'Operações de concessionário e pós-venda',
    kpi: 'Uma imagem operacional única entre marcas',
    description:
      'Unificação de dados de DMS, stock, pricing e pós-venda entre marcas e localizações; automação de fluxos documentais, logística de peças e agendamento de serviço sobre sistemas fragmentados.',
    icon: 'pill-green',
  },
  {
    id: 'telecom',
    category: 'Telecom & Media',
    badge: 'De Ponta a Ponta',
    title: 'Serviço ao cliente conversacional',
    kpi: 'Chamadas desviadas, CSAT sustentado',
    description:
      'Assistentes de voz e chat que tratam intenções de faturação e suporte de ponta a ponta, integrados com os sistemas existentes, automação sem perder qualidade de serviço.',
    icon: 'diamond-purple',
  },
  {
    id: 'logistics',
    category: 'Logística & Transportes',
    badge: 'A Tempo',
    title: 'Inteligência de supply chain e inventário',
    kpi: 'Menos stock, menos ruturas',
    description:
      'Leitura de procura, rebalanceamento de inventário e gestão de exceções em toda a rede, recomendações fundamentadas nos seus dados, executadas com padrões de aprovação.',
    icon: 'diamond-cyan',
  },
  {
    id: 'industrial',
    category: 'Indústria & Energia',
    badge: 'Preditivo',
    title: 'Copilotos de operações e manutenção',
    kpi: 'Maior disponibilidade e prontidão dos ativos',
    description:
      'Reunimos dados de sensores, registos de manutenção e manuais num só lugar; prevemos falhas e pomos as respostas nas mãos das equipas de terreno antes de os problemas escalarem.',
    icon: 'square-red',
  },
  {
    id: 'healthcare',
    category: 'Saúde & Ciências da Vida',
    badge: 'Unificado, em Tempo Real',
    title: 'Plataformas de dados clínicos e de investigação',
    kpi: 'Menos duplicação, investigação mais rápida',
    description:
      'Unificação de dados de investigação e operacionais fragmentados em acesso estruturado e em tempo real, reduzindo duplicação e acelerando fluxos científicos e clínicos.',
    icon: 'circle-orange',
  },
  {
    id: 'public',
    category: 'Setor Público',
    badge: 'Governado por Defeito',
    title: 'Processamento de cidadãos e processos',
    kpi: 'Filas mais curtas, auditabilidade total',
    description:
      'Automação da entrada, classificação e encaminhamento de pedidos e documentos, com os controlos de auditoria e acesso que as instituições públicas exigem.',
    icon: 'triangle-cyan',
  },
]

export const ENTERPRISE_AI_EN = {
  seo: {
    title: 'Enterprise AI Services',
    description:
      'LOG helps enterprises move AI from pilots to production with applied AI, governance, automation and forward deployed engineering teams.',
    ogTitle: 'Enterprise AI Services | LOG',
    ogDescription:
      'LOG helps enterprises move AI from pilots to production with applied AI, governance, automation and forward deployed engineering teams.',
  },
  hero: {
    eyebrow: 'Data & AI → Forward Deployed Engineers.',
    headlineDark: 'Most enterprise AI never ships.',
    headlineAccentLead: 'We built the',
    headlineAccentRest: 'loop that does and runs it for you.',
    ctaPrimary: 'Start Shipping',
    ctaSecondary: 'Discover How it Works',
  },
  logoStrip: {
    label: 'Teams that stopped piloting and started shipping',
  },
  value: {
    sectionTitle: 'Forward Deployed Engineers',
    heading: 'AI only counts when it shows up in the numbers.',
    lede:
      'We replace slow, high-cost manual workflows with robust software architecture,',
    ledeAccent:
      'turning underutilized enterprise data into a predictable competitive advantage.',
    ledeTail: 'No science projects. No vendor lock-in.',
    pillars: [
      {
        title: 'Save hours of manual work',
        body:
          'Eliminate overhead by automating heavy document handling, data extraction, classification, and reconciliation. Free your teams to focus on high-value tasks requiring human judgment.',
        icon: 'efficiency',
      },
      {
        title: 'Move faster, decide better',
        body:
          'Accelerate cycle times from hours to minutes. Implement secure RAG systems grounded exclusively in your corporate data to ensure accurate, context-aware decision-making.',
        icon: 'speed',
      },
      {
        title: 'Safe, secure, and compliant',
        body:
          'Privacy and security are built into the code from day one. Your company data stays completely private, safe, and fully aligned with European regulations like the EU AI Act and GDPR, allowing you to grow without any digital risks.',
        icon: 'governance',
      },
    ],
    manifesto:
      "We design practical software that eliminates your team's busiest manual work. By transforming slow routines into automated tasks, we help your business move smoothly without the headaches. No unfinished tech trials, no platform restrictions. Just tools you completely own.",
  },
  loop: {
    heading: 'A repeatable path from idea to value in production.',
    lede:
      ' we fix one practical problem first, hand the easy-to-use software over to your people, and then use what we learn together to simplify your next big daily task.',
    ledeAccent:
      "We don't deliver temporary prototypes that fade away. Our process is a continuous cycle:",
    centerLabel: 'AI Value Loop',
    steps: LOOP_STEPS_EN,
    defaultStepId: 'discover',
  },
  entryPaths: {
    headingLines: ['Three ways to enter the loop.', 'Each one ships.'],
    introLead:
      'These are not three products, they are the entry points into the same loop.',
    introNote:
      'Each has a fixed scope and a concrete outcome. You buy a step, and the next one only starts once the last has proven its value.',
    steps: [
      {
        id: 'diagnose',
        badge: 'Step 1 \u2013 Diagnose',
        title: 'Ai & Automation Diagnostic',
        description:
          'Where AI creates real value in your operation and where it does not.',
        featured: false,
        stats: [
          { label: 'Duration', value: '4 weeks' },
          { label: 'You leave with', value: 'A prioritised roadmap' },
        ],
        features: [
          'Process, data and AI-maturity mapping',
          'Use cases ranked by value and effort',
          'Investment case and sequencing plan',
        ],
        footer: 'The Discover phase: run as a standalone engagement',
      },
      {
        id: 'pilot',
        badge: 'Step 2 \u2013 Pilot',
        title: 'AI Automation Pilot',
        description:
          'One real use case, built and put live inside your operation.',
        featured: true,
        stats: [
          { label: 'Duration', value: '8\u201312 weeks' },
          { label: 'You leave with', value: 'A system in production' },
        ],
        features: [
          'A solution designed around your process',
          'Integrated with your existing systems',
          'Before/after impact against a baseline',
        ],
        footer: 'Discover \u00b7 Build \u00b7 Deploy, on a single case',
      },
      {
        id: 'scale',
        badge: 'Step 3 \u2013 Scale',
        title: 'Agentic Operating Model',
        description:
          'Multiple cases running, with an embedded squad and its own governance.',
        featured: false,
        stats: [
          { label: 'Duration', value: 'From 12\u201316 weeks' },
          { label: 'You leave with', value: 'An operation at scale' },
        ],
        features: [
          'An FDE squad embedded in your team',
          'Reusable assets compounding across cases',
          'Governance, cost and risk under control',
        ],
        footer: 'The full loop: running and compounding',
      },
    ],
    footnote:
      'Most partners start with the Diagnostic and expand from there. Scope, timeline and commercials are agreed before each step begins.',
  },
  fde: {
    heading: 'Forward Deployed Engineers that own the outcome.',
    lede:
      'The AI Value Loop is run by senior teams — Forward Deployed Engineers — that embed directly into your systems and workflows and stay accountable for results. You keep the code and the IP and we make sure the value keeps coming.',
    capabilities: FDE_CAPABILITIES_EN,
    defaultCapabilityId: 'automation',
  },
  metrics: {
    headingLines: ['Every loop we run targets', 'a measurable outcome.'],
    items: [
      {
        value: '−80%',
        label:
          'Manual effort on targeted document workflows, measured against the pre-AI baseline.',
      },
      {
        value: 'Weeks',
        label:
          'From a validated use case to a working system in production — not quarters, not roadmaps.',
      },
      {
        value: '100%',
        label:
          'Of the code and IP stays with you, fully transferable. We earn the next loop on results, not lock-in.',
      },
    ],
  },
  useCases: {
    heading:
      'Behind every number, a loop. Here\u2019s what applied AI looks like when it ships.',
    lede:
      'Browse by the capability you need, or by the sector you operate in. Every card targets a measurable outcome, not a feature.',
    tabSolution: 'By Solution',
    tabIndustry: 'By Industry',
    solution: USE_CASES_SOLUTION_EN,
    industry: USE_CASES_INDUSTRY_EN,
  },
  finalCta: {
    heading: 'Let\u2019s find where AI pays off first.',
    body:
      'In a short working session we\u2019ll map where AI delivers the fastest return in your business and how a single Value Loop can start capturing it.',
    cta: "Let's talk",
  },
}

export const ENTERPRISE_AI_PT = {
  seo: {
    title: 'Serviços de IA Empresarial',
    description:
      'A LOG ajuda empresas a levar IA de pilotos para produção com IA aplicada, governação, automação e equipas de engenharia integradas.',
    ogTitle: 'Serviços de IA Empresarial | LOG',
    ogDescription:
      'A LOG ajuda empresas a levar IA de pilotos para produção com IA aplicada, governação, automação e equipas de engenharia integradas.',
  },
  hero: {
    eyebrow: 'Dados & IA → Forward Deployed Engineers.',
    headlineDark: 'A maioria da IA empresarial nunca chega a produção.',
    headlineAccentLead: 'Construímos o',
    headlineAccentRest: 'loop que põe a IA empresarial a funcionar.',
    ctaPrimary: 'Comece a implementar',
    ctaSecondary: 'Descubra Como Funciona',
  },
  logoStrip: {
    label: 'Equipas que deixaram de estar em fase de teste e passaram à fase de lançamento',
  },
  value: {
    sectionTitle: 'Forward Deployed Engineers',
    heading: 'A IA só conta quando os números começam a aparecer.',
    lede:
      'Substituímos fluxos manuais lentos e dispendiosos por arquitetura de software robusta,',
    ledeAccent:
      'transformando dados empresariais subaproveitados numa vantagem competitiva previsível.',
    ledeTail: 'Sem projetos de laboratório. Sem dependência de fornecedor.',
    pillars: [
      {
        title: 'Poupe horas de trabalho manual',
        body:
          'Elimine sobrecarga ao automatizar o tratamento de documentos, extração de dados, classificação e reconciliação. Liberte as suas equipas para o trabalho de alto valor que exige discernimento humano.',
        icon: 'efficiency',
      },
      {
        title: 'Avance mais rápido, decida melhor',
        body:
          'Acelere ciclos de horas para minutos. Implemente sistemas RAG seguros, fundamentados exclusivamente nos dados da sua empresa, para decisões precisas e contextualizadas.',
        icon: 'speed',
      },
      {
        title: 'Seguro, protegido e em conformidade',
        body:
          'Privacidade e segurança estão incorporadas no código desde o primeiro dia. Os dados da sua empresa permanecem totalmente privados, protegidos e alinhados com regulamentação europeia como o AI Act e o RGPD, para crescer sem riscos digitais.',
        icon: 'governance',
      },
    ],
    manifesto:
      'Desenhamos software prático que elimina o trabalho manual mais exigente das suas equipas. Ao transformar rotinas lentas em tarefas automatizadas, ajudamos o seu negócio a avançar sem fricção. Sem provas de conceito incompletas, sem restrições de plataforma. Ferramentas que são inteiramente suas.',
  },
  loop: {
    heading: 'Um caminho repetível da ideia ao valor em produção.',
    lede:
      ' resolvemos primeiro um problema prático, entregamos o software às suas equipas e, com o que aprendemos em conjunto, simplificamos a próxima grande tarefa do dia a dia.',
    ledeAccent:
      'Não entregamos protótipos temporários que desaparecem. O nosso processo é um ciclo contínuo:',
    centerLabel: 'AI Value Loop',
    steps: LOOP_STEPS_PT,
    defaultStepId: 'discover',
  },
  entryPaths: {
    headingLines: ['Três formas de entrar no loop.', 'Cada uma entrega.'],
    introLead:
      'Não são três produtos, são os pontos de entrada no mesmo loop.',
    introNote:
      'Cada um tem um âmbito fixo e um resultado concreto. Compra um passo, e o seguinte só começa quando o anterior provou o seu valor.',
    steps: [
      {
        id: 'diagnose',
        badge: 'Passo 1 \u2013 Diagnosticar',
        title: 'Diagnóstico de IA e Automação',
        description:
          'Onde a IA cria valor real na sua operação e onde não cria.',
        featured: false,
        stats: [
          { label: 'Duração', value: '4 semanas' },
          { label: 'Fica com', value: 'Um roadmap priorizado' },
        ],
        features: [
          'Mapeamento de processos, dados e maturidade em IA',
          'Casos de uso ordenados por valor e esforço',
          'Caso de investimento e plano de sequenciação',
        ],
        footer: 'A fase Discover: como um engagement autónomo',
      },
      {
        id: 'pilot',
        badge: 'Passo 2 \u2013 Piloto',
        title: 'Piloto de Automação com IA',
        description:
          'Um caso de uso real, construído e colocado em produção na sua operação.',
        featured: true,
        stats: [
          { label: 'Duração', value: '8\u201312 semanas' },
          { label: 'Fica com', value: 'Um sistema em produção' },
        ],
        features: [
          'Uma solução desenhada à volta do seu processo',
          'Integrada nos seus sistemas existentes',
          'Impacto antes/depois face a uma baseline',
        ],
        footer: 'Discover \u00b7 Build \u00b7 Deploy, num único caso',
      },
      {
        id: 'scale',
        badge: 'Passo 3 \u2013 Escalar',
        title: 'Modelo Operacional Agêntico',
        description:
          'Vários casos em execução, com uma equipa integrada e governação própria.',
        featured: false,
        stats: [
          { label: 'Duração', value: 'A partir de 12\u201316 semanas' },
          { label: 'Fica com', value: 'Uma operação em escala' },
        ],
        features: [
          'Uma equipa FDE integrada na sua equipa',
          'Ativos reutilizáveis que se acumulam entre casos',
          'Governação, custo e risco sob controlo',
        ],
        footer: 'O loop completo: a correr e a compor valor',
      },
    ],
    footnote:
      'A maioria dos parceiros começa pelo Diagnóstico e expande a partir daí. Âmbito, cronograma e condições comerciais são acordados antes de cada passo começar.',
  },
  fde: {
    heading: 'Forward Deployed Engineers que respondem pelo resultado.',
    lede:
      'O AI Value Loop é executado por equipas seniores, Forward Deployed Engineers, que se integram diretamente nos seus sistemas e fluxos de trabalho e permanecem responsáveis pelos resultados. O código e a propriedade intelectual ficam consigo e nós garantimos que o valor continua a chegar.',
    capabilities: FDE_CAPABILITIES_PT,
    defaultCapabilityId: 'automation',
  },
  metrics: {
    headingLines: ['Cada loop que corremos visa', 'um resultado mensurável.'],
    items: [
      {
        value: '−80%',
        label:
          'Redução de esforço manual nos fluxos documentais visados, medido contra a baseline pré-IA.',
      },
      {
        value: 'Semanas',
        label:
          'De um caso de uso validado a um sistema em produção, não trimestres, não roadmaps.',
      },
      {
        value: '100%',
        label:
          'Do código e IP ficam consigo, totalmente transferíveis. Ganhamos o loop seguinte com resultados, não com dependência.',
      },
    ],
  },
  useCases: {
    heading:
      'Por trás de cada número, um loop. Isto é IA aplicada quando chega a produção.',
    lede:
      'Explore pelo caso de uso que procura ou pelo sector em que opera.',
    tabSolution: 'Por Solução',
    tabIndustry: 'Por Setor',
    solution: USE_CASES_SOLUTION_PT,
    industry: USE_CASES_INDUSTRY_PT,
  },
  finalCta: {
    heading: 'Vamos encontrar onde a IA compensa primeiro.',
    body:
      'Numa breve sessão de trabalho mapeamos onde a IA entrega o retorno mais rápido no seu negócio e como um único Value Loop pode começar a capturá-lo.',
    cta: 'Vamos falar',
  },
}

export const getEnterpriseAIContent = lang =>
  lang === 'en' ? ENTERPRISE_AI_EN : ENTERPRISE_AI_PT

export const ENTERPRISE_AI_LOGOS = [
  { src: 'ericsson', alt: 'Ericsson', width: 62, height: 54 },
  { src: 'ageas', alt: 'Ageas', width: 102, height: 41 },
  { src: 'worten', alt: 'Worten', width: 102, height: 21 },
  { src: 'pfizer', alt: 'Pfizer', width: 102, height: 42 },
  { src: 'nos', alt: 'NOS', width: 84, height: 46 },
  { src: 'sonae', alt: 'Sonae', width: 102, height: 19 },
]
